import { Button, Col, Form, Input, Row, Select, Divider, Skeleton, Switch } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { createMarket, getMarketById, getMarketByIdSuccess, updateMarket } from "../../store/actions/market.actions";
import { COUNTRY_LIST, VALIDATE_FORM_MESSAGES_TEMPLATE, mapLabelToT } from "../../utils/constants";
import "./AddEditMarket.scss";
import { sortAlphabetically, validateCode, getFeatureFlags } from "../../utils/utils";


const AddEditMarket = ({ marketId, onSuccess, onCloseModal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { value: market, loading } = useSelector(store => store.market);
  const [form] = Form.useForm();
  const [fetchingMarket, setFetchingMarket] = useState(false);
  const FEATURE_FLAG = getFeatureFlags();
  const aiAgentPilotMarketsFeature = FEATURE_FLAG?.["AI Agent-Pilot Markets"];

  const onFormFinish = async fieldValues => {
    const values = {
      ...fieldValues,
      ...(aiAgentPilotMarketsFeature?.status ? { agentPilotMarket: fieldValues.agentPilotMarket || false } : {}),
    };
    if (marketId) {
      await dispatch(updateMarket(marketId, values));
    } else {
      const isActive = values.isActive || false;
      values.isActive = isActive;
      await dispatch(createMarket(values));
    }
    onCloseModal(false);
    if (onSuccess) {
      onSuccess();
    }
  };

  useEffect(() => {
    const didMount = async () => {
      if (marketId) {
        setFetchingMarket(true);
        await dispatch(getMarketById(marketId));
        setFetchingMarket(false);
      }
    };
    didMount();
  }, []);

  useEffect(
    () => () => {
      form.resetFields();
    },
    [],
  );

  const isValidCode = (rule, value) => {
    if (value && !validateCode(value)) {
      return Promise.reject(new Error(t("AddEditMarket.MarketCodeFormat")));
    }
    return Promise.resolve();
  };

  return (
    <div className="edit-market">
      <Skeleton loading={fetchingMarket} active paragraph={{ rows: 6 }}>
        <Form
          form={form}
          className="edit-market"
          layout="vertical"
          name="nest-messages"
          initialValues={market}
          onFinish={onFormFinish}
          validateMessages={VALIDATE_FORM_MESSAGES_TEMPLATE}
        >
          <Row className="fields-row" gutter={20} type="flex">
            <Col xs={24} sm={12} md={12} lg={12}>
              <Form.Item
                label={t("AddEditRegion.RegionName")}
                name={["region", "name"]}
                validateFirst
                rules={[{ required: true, whitespace: true }]}
              >
                <Input placeholder={t("AddEditRegion.RegionName")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Form.Item label={t("AddEditRegion.RegionCode")} name={["region", "code"]}>
                <Input placeholder={t("AddEditRegion.RegionCode")} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Form.Item label={t("Property.Country")} name="country">
                <Select
                  placeholder={t("AddEditMarket.CountryPlaceholder")}
                  options={sortAlphabetically(mapLabelToT(COUNTRY_LIST, "COUNTRY_LIST"))}
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Form.Item label={t("AddEditMarket.MarketCode")} name="id">
                <Input placeholder={t("AddEditMarket.MarketCodePlaceholder")} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Form.Item
                label={t("AddEditMarket.MarketName")}
                name="marketName"
                validateFirst
                rules={[{ required: true, whitespace: true }]}
              >
                <Input placeholder={t("AddEditMarket.MarketName")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Row gutter={20}>
                <Col xs={12} sm={12} md={8} lg={8}>
                  <Form.Item label={t("AddEditMarket.IsActive")} name="isActive" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                {aiAgentPilotMarketsFeature?.status && (
                  <Col xs={12} sm={12} md={12} lg={12}>
                    <Form.Item
                      label={t("AddEditMarket.AgentPilotMarket")}
                      name="agentPilotMarket"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Col>

            <Col xs={24} sm={24} md={24} lg={24}>
              <Divider>Submarkets</Divider>
              <Form.List name="subMarkets">
                {(fields, subOpt) => (
                  <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
                    {fields.map(field => (
                      <div style={{ display: "flex", gap: "8px" }}>
                        <Form.Item
                          label={t("AddEditMarket.SubmarketCode")}
                          name={[field.name, "id"]}
                          rules={[{ required: true, whitespace: true }]}
                          style={{ display: "inline-block", width: "calc(50% - 8px)" }}
                        >
                          <Input placeholder={t("AddEditMarket.SubmarketCodePlaceHolder")} />
                        </Form.Item>
                        <Form.Item
                          label={t("AddEditMarket.SubmarketName")}
                          name={[field.name, "subMarketName"]}
                          rules={[{ whitespace: true }]}
                          style={{ display: "inline-block", width: "calc(50% - 8px)" }}
                        >
                          <Input placeholder={t("AddEditMarket.SubmarketName")} />
                        </Form.Item>
                        <DeleteOutlined onClick={() => subOpt.remove(field.name)} />
                      </div>
                    ))}
                    <Form.Item style={{ justifyContent: "center", alignItems: "end" }}>
                      <Button size="medium" onClick={() => subOpt.add()} block>
                        Add Submarket
                      </Button>
                    </Form.Item>
                  </div>
                )}
              </Form.List>
              <Divider />
            </Col>
            <Col span={24}></Col>
            <Col span={24}>
              <Row className="fields-row" gutter={20} type="flex" justify="center">
                <Col xs={12} sm={12} md={8} lg={8}>
                  <Button
                    block
                    onClick={() => {
                      onCloseModal(false);
                      dispatch(getMarketByIdSuccess({}));
                    }}
                  >
                    {t("Buttons.DiscardChanges")}
                  </Button>
                </Col>
                <Col xs={12} sm={12} md={8} lg={8}>
                  <Button block type="primary" htmlType="submit" loading={loading}>
                    {marketId ? t("Buttons.SaveChanges") : t("Buttons.Create")}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </div>
  );
};

AddEditMarket.propTypes = {
  marketId: PropTypes.string,
  onSuccess: PropTypes.func,
  onCloseModal: PropTypes.func,
};

export default AddEditMarket;
